import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button as AriaButton, MenuItem as AriaMenuItem } from 'react-aria-components'
import { toast } from 'sonner'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@untitledui-icons/react/line'

import { Dropdown } from '@/components/base/dropdown/dropdown'
import { cn } from '@/lib/utils'
import {
  useAssignWidgetTemplate,
  type WidgetOverviewRow,
  type WidgetTemplate,
} from '@/hooks/api/useWidgets'

/** The menu shows the templates in widest use; the rest live behind "see all". */
const MENU_TEMPLATE_LIMIT = 4

interface WidgetTemplateMenuProps {
  row: WidgetOverviewRow
  templates: WidgetTemplate[]
  onSeeAll: () => void
}

interface MenuRowProps {
  title: string
  description: string
  trailing?: React.ReactNode
  textValue: string
  isDisabled?: boolean
  onAction: () => void
}

/** Two-line menu row — the vendored `Dropdown.Item` is single-line only. */
function MenuRow({ title, description, trailing, textValue, isDisabled, onAction }: MenuRowProps) {
  return (
    <AriaMenuItem
      id={textValue}
      textValue={textValue}
      isDisabled={isDisabled}
      onAction={onAction}
      className={(state) =>
        cn(
          'mx-1.5 my-px flex cursor-pointer items-start gap-3 rounded-[6px] px-2.5 py-2 text-left outline-none',
          state?.isFocused && 'bg-[#fafaf9] dark:bg-gray-800',
          state?.isDisabled && 'cursor-not-allowed opacity-60'
        )
      }
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="block truncate text-sm leading-5 font-semibold text-utility-gray-700 dark:text-gray-200">
          {title}
        </span>
        <span className="block truncate text-[13px] leading-[18px] text-[#6a6a6a] dark:text-gray-400">
          {description}
        </span>
      </span>
      {trailing ? (
        <span className="text-utility-gray-700 mt-0.5 shrink-0 dark:text-gray-300">{trailing}</span>
      ) : null}
    </AriaMenuItem>
  )
}

function templateChipLabel(name: string, defaultShort: string): string {
  return /^default template$/i.test(name) ? defaultShort : name
}

/**
 * The widget template cell: a chip showing the current template that opens a
 * menu of the customer's templates. Assigning only changes styling — the
 * assistant keeps its widget key and its WhatsApp number.
 */
export function WidgetTemplateMenu({ row, templates, onSeeAll }: WidgetTemplateMenuProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const base = 'resources.widgets.template_menu'
  const assignTemplate = useAssignWidgetTemplate()

  const isLive = row.connection_state === 'live'

  const ranked = templates
    .filter((template) => template.customer_id === row.customer_id)
    .sort((a, b) => b.assignments.length - a.assignments.length)
  const shown = ranked.slice(0, MENU_TEMPLATE_LIMIT)
  const hidden = ranked.length - shown.length

  const handleAssign = async (template: WidgetTemplate) => {
    if (template.id === row.template_id) {
      return
    }

    try {
      await assignTemplate.mutateAsync({ assistant_id: row.assistant_id, template_id: template.id })
      toast.success(t('resources.widgets.picker.assigned_toast', { template: template.name }))
    } catch {
      toast.error(t('resources.widgets.picker.assign_failed_toast'))
    }
  }

  const describe = (template: WidgetTemplate) => {
    const count = t('resources.widgets.templates.assistants_count', {
      count: template.assignments.length,
    })
    return template.kind === 'multi'
      ? `${count} · ${t('resources.widgets.templates.multi_venue_short')}`
      : count
  }

  /** Same metrics as the live chip, muted, so the row keeps its rhythm. */
  const chip =
    'inline-flex h-[30px] max-w-full items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium'

  if (!isLive) {
    return (
      <span
        title={t('resources.widgets.assistants.template_needs_connection')}
        className={cn(
          chip,
          'border-dashed border-gray-300 text-gray-400 dark:border-gray-700 dark:text-gray-500'
        )}
      >
        <span className="truncate">
          {row.template_name ?? t('resources.widgets.assistants.select_template')}
        </span>
      </span>
    )
  }

  return (
    <Dropdown.Root>
      <AriaButton
        className={cn(
          chip,
          'cursor-pointer border-gray-200 bg-white text-gray-600 outline-none',
          'hover:bg-[#fafaf9] focus-visible:ring-2 focus-visible:ring-gray-300',
          'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800',
          assignTemplate.isPending && 'opacity-60'
        )}
      >
        <span className="truncate">
          {row.template_name
            ? templateChipLabel(row.template_name, t(`${base}.default_short`))
            : t('resources.widgets.assistants.select_template')}
        </span>
        <ChevronDownIcon className="size-3.5 shrink-0" />
      </AriaButton>

      <Dropdown.Popover
        placement="bottom end"
        className="w-[260px] overflow-hidden rounded-md shadow-lg ring-1 ring-black/8"
      >
        <Dropdown.Menu className="py-1" selectionMode="none" disallowEmptySelection={false}>
          {shown.map((template) => {
            const isCurrent = template.id === row.template_id

            return (
              <MenuRow
                key={template.id}
                textValue={template.name}
                title={template.name}
                description={describe(template)}
                trailing={isCurrent ? <CheckIcon className="size-[18px]" /> : undefined}
                onAction={() => void handleAssign(template)}
              />
            )
          })}

          {hidden > 0 && (
            <MenuRow
              textValue={t(`${base}.see_all`)}
              title={t(`${base}.see_all`)}
              description={t(`${base}.see_all_description`, { count: ranked.length })}
              trailing={<ChevronRightIcon className="size-[18px]" />}
              onAction={onSeeAll}
            />
          )}

          <Dropdown.Separator className="mx-1.5 my-1 w-auto bg-[#e7e5e4]" />

          <MenuRow
            textValue={t(`${base}.create`)}
            title={t(`${base}.create`)}
            description={t(`${base}.create_description`)}
            trailing={<PlusIcon className="size-[18px]" />}
            onAction={() =>
              navigate(
                `/widgets/templates/new?assign=${row.assistant_id}&customer=${row.customer_id}`
              )
            }
          />
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}
